import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, ExternalLink, MessageCircle, X, ArrowUpRight, BookOpen } from "lucide-react";
import {
  SKILLS_CONTENT,
  EDUCATION_CONTENT,
  TESTIMONIALS_CONTENT,
  MEDIA_CONTENT,
  GALLERY_CONTENT,
  SOCIAL_CONTENT,
  BLOG_CONTENT,
  MOREINFO_CONTENT,
  EXPOSURE_SOCIAL_CONTENT,
} from "../moreContent";

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
};

const Eyebrow = ({ children }) => (
  <p className="rp-eyebrow mb-4">{children}</p>
);

const SectionTitle = ({ children }) => (
  <h3
    className="rp-display text-3xl md:text-4xl font-light text-[#262016] leading-[1.1] mb-2"
    style={{ textWrap: "balance" }}
  >
    {children}
  </h3>
);

const Intro = ({ children }) =>
  children ? (
    <p
      className="rp-display rp-italic text-base md:text-lg text-[#5C5042] max-w-2xl mb-8"
      style={{ textWrap: "pretty" }}
    >
      {children}
    </p>
  ) : null;

// ---------- Skills & Tools ----------
function SkillsSubsection() {
  const levelColor = {
    Expert: "#B5793F",
    Advanced: "#23C795",
    Competent: "#19B5DE",
    Intermediate: "#8A7E6B",
    Beginner: "#262016",
  };
  return (
    <motion.div {...fadeUp} data-testid="more-skills">
      <Eyebrow>Skills & Tools</Eyebrow>
      <SectionTitle>Built with range. Sharpened by reps.</SectionTitle>
      <Intro>A clear view of core competencies, software fluency, and the lens shaped by 15 years of mixed roles.</Intro>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
        <div className="lg:col-span-5">
          <p className="rp-block-title">Core competencies</p>
          <p
            className="mb-4 text-[15px] md:text-base font-semibold text-[#262016] leading-relaxed"
            style={{ textWrap: "balance" }}
            data-testid="skills-intro-line"
          >
            At the intersection of design thinking, strategy, AI, effective stakeholder communication, team building, execution, delivery and ownership.
          </p>
          <ul className="flex flex-wrap gap-2">
            {SKILLS_CONTENT.competencies.map((s) => (
              <li
                key={s}
                className="px-3.5 py-1.5 rounded-full text-sm text-[#262016] bg-white/65 border border-[#26201618]"
              >
                {s}
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-7">
          <p className="rp-block-title">Tools &amp; software</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {SKILLS_CONTENT.tools.map((t) => (
              <div
                key={t.name}
                className="rp-foam bg-white/65 px-4 py-3"
              >
                <p className="text-sm font-medium text-[#262016] leading-tight">{t.name}</p>
                <p
                  className="text-[10px] tracking-[0.16em] uppercase mt-1.5"
                  style={{ color: levelColor[t.level] || "#8A7E6B" }}
                >
                  {t.level}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10">
        <p className="rp-block-title">Proficiency legend</p>
        <ul className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm text-[#5C5042]">
          {SKILLS_CONTENT.legend.map(([level, desc]) => (
            <li key={level} className="flex flex-col">
              <span
                className="text-[10px] tracking-[0.16em] uppercase mb-0.5"
                style={{ color: levelColor[level] }}
              >
                {level}
              </span>
              <span>{desc}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

// ---------- Education ----------
function EducationSubsection() {
  const [certIdx, setCertIdx] = useState(null);
  const certs = EDUCATION_CONTENT.certificates;
  const closeLightbox = () => setCertIdx(null);
  const prevCert = () => setCertIdx((i) => (i === null ? null : (i - 1 + certs.length) % certs.length));
  const nextCert = () => setCertIdx((i) => (i === null ? null : (i + 1) % certs.length));

  React.useEffect(() => {
    if (certIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") prevCert();
      else if (e.key === "ArrowRight") nextCert();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [certIdx]);

  return (
    <motion.div {...fadeUp} data-testid="more-education">
      <Eyebrow>Education &amp; Credentials</Eyebrow>
      <SectionTitle>Formal foundations, plus continuous upskilling.</SectionTitle>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8">
        <div className="lg:col-span-6">
          <p className="rp-block-title">Formal education</p>
          {EDUCATION_CONTENT.formal.map((e, i) => (
            <div key={i} className="rp-foam bg-white/65 p-5" style={{ marginTop: i === 0 ? 0 : 24 }}>
              <p className="rp-display text-lg text-[#262016] leading-snug">{e.degree}</p>
              <p className="text-sm text-[#5C5042] mt-1">{e.institute}</p>
              {e.affiliation && (
                <p className="text-xs text-[#8A7E6B] mt-0.5 italic">{e.affiliation}</p>
              )}
              <p className="text-[10px] tracking-[0.16em] uppercase text-[#B5793F] mt-3">{e.year}</p>
              {e.awards && (
                <ul className="mt-3 space-y-1 text-sm text-[#262016]">
                  {e.awards.map((a) => (
                    <li key={a} className="flex items-start gap-2">
                      <span className="text-[#B5793F]">✦</span>
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
        <div className="lg:col-span-6">
          <p className="rp-block-title">Upskilling &amp; certifications</p>
          {EDUCATION_CONTENT.upskilling.map((u, i) => (
            <div key={i} className="rp-foam bg-white/65 p-5" style={{ marginTop: i === 0 ? 0 : 24 }}>
              <p className="rp-display text-lg text-[#262016] leading-snug">{u.title}</p>
              <p className="text-sm text-[#5C5042] mt-1">{u.issuer}</p>
              <p className="text-[10px] tracking-[0.16em] uppercase text-[#23C795] mt-3">{u.status}</p>
              {u.note && (
                <p className="text-sm rp-italic text-[#5C5042] mt-2" style={{ textWrap: "pretty" }}>
                  {u.note}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <p className="rp-block-title">Recognitions &amp; certificates</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {certs.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setCertIdx(i)}
              className="group rp-foam overflow-hidden bg-white/70 p-2 md:p-2.5 flex flex-col cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-[#B5793F]/60 transition-transform hover:-translate-y-0.5"
              data-testid={`cert-${i}`}
              aria-label={`Open certificate ${i + 1}`}
            >
              <div className="w-full aspect-[4/3] overflow-hidden rounded-md bg-[#F6F1E5] flex items-center justify-center">
                <img
                  src={src}
                  alt={`Certificate ${i + 1}`}
                  className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                  loading="lazy"
                />
              </div>
            </button>
          ))}
        </div>
      </div>

      {certIdx !== null && (
        <CertificateLightbox
          certs={certs}
          index={certIdx}
          onClose={closeLightbox}
          onPrev={prevCert}
          onNext={nextCert}
          onJump={setCertIdx}
        />
      )}
    </motion.div>
  );
}

function CertificateLightbox({ certs, index, onClose, onPrev, onNext, onJump }) {
  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-8"
      style={{ background: "rgba(15,11,6,0.94)", backdropFilter: "blur(14px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="cert-lightbox"
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close gallery"
        data-testid="cert-lightbox-close"
        className="absolute top-5 right-5 md:top-7 md:right-7 z-10 w-11 h-11 rounded-full flex items-center justify-center bg-[#EDE7D9] text-[#262016] hover:scale-105 transition-transform"
      >
        <X size={20} />
      </button>

      {/* Prev */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label="Previous certificate"
        data-testid="cert-lightbox-prev"
        className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center bg-[#EDE7D9]/90 text-[#262016] hover:bg-[#EDE7D9] transition-colors"
      >
        <ArrowUpRight size={20} className="-rotate-[135deg]" />
      </button>

      {/* Next */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label="Next certificate"
        data-testid="cert-lightbox-next"
        className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center bg-[#EDE7D9]/90 text-[#262016] hover:bg-[#EDE7D9] transition-colors"
      >
        <ArrowUpRight size={20} className="rotate-45" />
      </button>

      {/* Content */}
      <ZoomableImage
        src={certs[index]}
        index={index}
      />

      {/* Meta + thumbnails — positioned at the bottom, above the image controls area */}
      <div
        className="absolute left-0 right-0 bottom-4 md:bottom-6 flex flex-col items-center gap-3 pointer-events-none px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-wrap justify-center gap-2 max-w-full pointer-events-auto">
          {certs.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => { e.stopPropagation(); onJump(i); }}
              aria-label={`Jump to certificate ${i + 1}`}
              data-testid={`cert-lightbox-thumb-${i}`}
              className={`w-12 h-12 md:w-14 md:h-14 rounded-md overflow-hidden border-2 transition-all ${
                i === index
                  ? "border-[#B5793F] scale-[1.06]"
                  : "border-transparent opacity-60 hover:opacity-100"
              }`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#EDE7D9]/70">
          Scroll / pinch to zoom &middot; drag to pan
        </p>
      </div>
    </div>
  );
}

// Zoomable / pannable certificate image (scroll-wheel + pinch to zoom, drag to pan).
function ZoomableImage({ src, index }) {
  const [scale, setScale] = React.useState(1);
  const [pos, setPos] = React.useState({ x: 0, y: 0 });
  const dragRef = React.useRef({ dragging: false, startX: 0, startY: 0, ox: 0, oy: 0 });
  const pinchRef = React.useRef({ dist: 0, startScale: 1 });

  // Reset zoom whenever the certificate changes.
  React.useEffect(() => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  }, [index]);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const applyScale = (next, cx, cy) => {
    const clamped = clamp(next, 1, 5);
    if (clamped === scale) return;
    // Zoom towards cursor / pinch center.
    const ratio = clamped / scale;
    setPos((p) => ({
      x: cx !== undefined ? (p.x - cx) * ratio + cx : p.x * ratio,
      y: cy !== undefined ? (p.y - cy) * ratio + cy : p.y * ratio,
    }));
    setScale(clamped);
    if (clamped === 1) setPos({ x: 0, y: 0 });
  };

  const onWheel = (e) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = e.clientX - rect.left - rect.width / 2 - pos.x;
    const cy = e.clientY - rect.top - rect.height / 2 - pos.y;
    const delta = -e.deltaY * 0.0015;
    applyScale(scale * (1 + delta), cx, cy);
  };

  const onMouseDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, ox: pos.x, oy: pos.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.dragging) return;
    setPos({
      x: dragRef.current.ox + (e.clientX - dragRef.current.startX),
      y: dragRef.current.oy + (e.clientY - dragRef.current.startY),
    });
  };
  const onMouseUp = () => { dragRef.current.dragging = false; };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current = { dist: Math.hypot(dx, dy), startScale: scale };
    } else if (e.touches.length === 1 && scale > 1) {
      dragRef.current = {
        dragging: true,
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        ox: pos.x,
        oy: pos.y,
      };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current.dist) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const nextScale = pinchRef.current.startScale * (dist / pinchRef.current.dist);
      applyScale(nextScale);
    } else if (e.touches.length === 1 && dragRef.current.dragging) {
      setPos({
        x: dragRef.current.ox + (e.touches[0].clientX - dragRef.current.startX),
        y: dragRef.current.oy + (e.touches[0].clientY - dragRef.current.startY),
      });
    }
  };
  const onTouchEnd = () => {
    dragRef.current.dragging = false;
    pinchRef.current.dist = 0;
  };

  const onDoubleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = e.clientX - rect.left - rect.width / 2 - pos.x;
    const cy = e.clientY - rect.top - rect.height / 2 - pos.y;
    applyScale(scale > 1 ? 1 : 2.5, cx, cy);
  };

  return (
    <div
      className="relative w-full max-w-5xl flex items-center justify-center overflow-hidden select-none touch-none"
      style={{ height: "80vh" }}
      onWheel={onWheel}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onDoubleClick={onDoubleClick}
      onClick={(e) => e.stopPropagation()}
      data-testid="cert-lightbox-zoomstage"
    >
      <img
        src={src}
        alt={`Certificate ${index + 1}`}
        draggable={false}
        className="max-h-full w-auto max-w-full object-contain rounded-lg shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]"
        style={{
          transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
          transition: dragRef.current.dragging || pinchRef.current.dist ? "none" : "transform 0.18s ease-out",
          cursor: scale > 1 ? "grab" : "zoom-in",
          willChange: "transform",
        }}
        data-testid="cert-lightbox-image"
      />
    </div>
  );
}

// ---------- Testimonials ----------
function TestimonialsSubsection() {
  return (
    <motion.div {...fadeUp} data-testid="more-testimonials">
      <Eyebrow>Testimonials (as received on LinkedIn)</Eyebrow>
      <p
        className="rp-display rp-italic text-sm md:text-base text-[#5C5042] mb-8"
        style={{ textWrap: "nowrap", whiteSpace: "normal" }}
      >
        {TESTIMONIALS_CONTENT.intro}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mt-6">
        {TESTIMONIALS_CONTENT.items.map((t, i) => {
          const initials = t.name
            .split(/\s+/)
            .map((w) => w[0])
            .filter(Boolean)
            .slice(0, 2)
            .join("")
            .toUpperCase();
          return (
            <div key={i} className="rp-foam bg-white/65 p-6 md:p-7 flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <div className="relative w-12 h-12 shrink-0">
                  {t.avatar ? (
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="absolute inset-0 w-12 h-12 rounded-full object-cover border border-[#26201620] bg-[#EDE7D9]"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : null}
                  <div
                    aria-hidden={!!t.avatar}
                    className="w-12 h-12 rounded-full border border-[#26201620] bg-[#EDE7D9] flex items-center justify-center rp-display text-sm text-[#262016]"
                  >
                    {initials}
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="rp-display text-base text-[#262016] leading-tight">{t.name}</p>
                  <p className="text-xs text-[#8A7E6B] mt-0.5" style={{ textWrap: "pretty" }}>{t.role}</p>
                </div>
              </div>
              <p className="text-sm md:text-[15px] text-[#262016] leading-relaxed flex-1" style={{ textWrap: "pretty" }}>
                &ldquo;{t.quote}&rdquo;
              </p>
              {t.contact && (
                <a
                  href={t.contact}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-5 text-xs tracking-[0.14em] uppercase text-[#B5793F] hover:text-[#262016] transition-colors"
                  data-testid={`testimonial-contact-${i}`}
                >
                  <MessageCircle size={13} /> Verify directly
                </a>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

// ---------- In The Media ----------
function MediaSubsection() {
  const videos = MEDIA_CONTENT.items.filter((m) => m.type === "video");
  const articles = MEDIA_CONTENT.items.filter((m) => m.type !== "video");
  return (
    <motion.div {...fadeUp} data-testid="more-media">
      <Eyebrow>In the Media</Eyebrow>
      <SectionTitle>Mentions, features, conversations.</SectionTitle>
      <Intro>{MEDIA_CONTENT.intro}</Intro>

      <p className="rp-block-title mt-6">Video appearances</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {videos.map((m, i) => (
          <a
            key={i}
            href={m.href}
            target="_blank"
            rel="noopener noreferrer"
            className="group rp-foam overflow-hidden bg-white/65 block"
            data-testid={`media-video-${i}`}
          >
            <div className="relative aspect-video bg-[#1A1610]">
              {m.thumb ? (
                <img src={m.thumb} alt={m.title} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" loading="lazy" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#2a2218] to-[#1a1610]">
                  <Play size={28} className="text-[#EDE7D9]/70" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-[10px] tracking-[0.16em] uppercase bg-[#EDE7D9]/90 text-[#262016]">
                Video
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-[#EDE7D9]/95 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play size={18} className="ml-0.5 text-[#262016]" />
                </div>
              </div>
            </div>
            <p className="p-4 text-sm text-[#262016] leading-snug" style={{ textWrap: "pretty" }}>
              {m.title}
            </p>
          </a>
        ))}
      </div>

      <p className="rp-block-title mt-10">Press &amp; features</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {articles.map((m, i) => (
          <a
            key={i}
            href={m.href}
            target="_blank"
            rel="noopener noreferrer"
            className="group rp-foam bg-white/65 p-5 flex gap-4 hover:bg-white/80 transition-colors"
            data-testid={`media-article-${i}`}
          >
            {m.thumb && (
              <img src={m.thumb} alt={m.title} className="w-20 h-20 object-cover rounded-md shrink-0" loading="lazy" />
            )}
            <div className="flex-1">
              <span className="text-[10px] tracking-[0.16em] uppercase text-[#B5793F]">{m.type === "article" ? "Article" : "Feature"}</span>
              <p className="rp-display text-base text-[#262016] mt-1 leading-snug">{m.title}</p>
              {m.publisher && (
                <p className="text-xs text-[#8A7E6B] mt-1">{m.publisher}</p>
              )}
              <span className="inline-flex items-center gap-1 text-xs text-[#262016] mt-3 group-hover:text-[#B5793F] transition-colors">
                Read <ExternalLink size={11} />
              </span>
            </div>
          </a>
        ))}
      </div>
    </motion.div>
  );
}

// ---------- Gallery ----------
function GallerySubsection() {
  const [lightbox, setLightbox] = useState(null);
  return (
    <motion.div {...fadeUp} data-testid="more-gallery">
      <Eyebrow>Gallery</Eyebrow>
      <SectionTitle>Snapshots from the road.</SectionTitle>
      <Intro>{GALLERY_CONTENT.intro}</Intro>

      <div className="columns-2 md:columns-3 lg:columns-4 gap-3 [&>*]:mb-3">
        {GALLERY_CONTENT.images.map((src, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setLightbox(src)}
            className="block w-full overflow-hidden rounded-lg break-inside-avoid focus:outline-none focus:ring-2 focus:ring-[#B5793F]/60"
            data-testid={`gallery-img-${i}`}
          >
            <img
              src={src}
              alt={`Gallery ${i + 1}`}
              className="w-full h-auto object-cover hover:scale-[1.02] transition-transform duration-500"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </button>
        ))}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(20,16,10,0.92)", backdropFilter: "blur(8px)" }}
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-5 right-5 w-11 h-11 rounded-full flex items-center justify-center bg-[#EDE7D9] text-[#262016]"
            aria-label="Close"
          >
            <X size={20} />
          </button>
          <img
            src={lightbox}
            alt="Expanded"
            className="max-w-full max-h-full rounded-xl shadow-2xl object-contain"
          />
        </div>
      )}
    </motion.div>
  );
}

// ---------- Social Initiatives ----------
function SocialSubsection() {
  return (
    <motion.div {...fadeUp} data-testid="more-social">
      <Eyebrow>Social Initiatives</Eyebrow>
      <SectionTitle>Giving back, on repeat.</SectionTitle>
      <Intro>{SOCIAL_CONTENT.intro}</Intro>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
        {SOCIAL_CONTENT.initiatives.map((s, i) => (
          <div key={i} className="rp-foam bg-white/65 overflow-hidden flex flex-col">
            {s.cover && (
              <div className="aspect-[5/3] overflow-hidden">
                <img src={s.cover} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
            )}
            <div className="p-5 flex flex-col flex-1">
              <span className="text-[10px] tracking-[0.16em] uppercase text-[#B5793F]">{s.tag}</span>
              <p className="rp-display text-lg text-[#262016] mt-1.5 leading-snug">{s.title}</p>
              <p className="text-sm text-[#5C5042] mt-2 leading-relaxed flex-1" style={{ textWrap: "pretty" }}>
                {s.body}
              </p>
              {s.href && (
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#262016] mt-4 hover:text-[#B5793F]"
                >
                  Read more <ExternalLink size={11} />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

// ---------- Blog ----------
function BlogSubsection() {
  const [active, setActive] = useState(null);
  return (
    <motion.div {...fadeUp} data-testid="more-blog">
      <Eyebrow>Blog</Eyebrow>
      <SectionTitle>Expressions on experiences, thoughts, learnings.</SectionTitle>
      <Intro>Short reads from a long career — operator notes on craft, AI, leadership, and the slow math of building things that last.</Intro>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mt-2">
        {BLOG_CONTENT.posts.map((p, i) => (
          <button
            key={p.slug || i}
            type="button"
            onClick={() => setActive(p)}
            className="rp-foam bg-white/65 p-6 md:p-7 flex flex-col group hover:bg-white/85 transition-colors text-left"
            data-testid={`blog-post-${i}`}
          >
            <div className="flex items-center justify-between text-[10px] tracking-[0.16em] uppercase mb-1">
              <span className="text-[#B5793F] inline-flex items-center gap-1.5">
                <BookOpen size={13} /> {p.readTime}
              </span>
              {p.tag && (
                <span className="px-2 py-0.5 rounded-full bg-[#EDE7D9] text-[#5C5042]">
                  {p.tag}
                </span>
              )}
            </div>
            {p.date && (
              <span className="text-[10px] tracking-[0.16em] uppercase text-[#8A7E6B] mb-2">{p.date}</span>
            )}
            <p className="rp-display text-xl md:text-2xl text-[#262016] leading-snug mt-1" style={{ textWrap: "balance" }}>
              {p.title}
            </p>
            <p className="text-sm text-[#5C5042] mt-3 leading-relaxed flex-1" style={{ textWrap: "pretty" }}>
              {p.excerpt}
            </p>
            <span className="inline-flex items-center gap-1 text-xs text-[#262016] mt-5 group-hover:text-[#B5793F]">
              Read article <ArrowUpRight size={12} />
            </span>
          </button>
        ))}
      </div>

      {active && <BlogArticleModal post={active} onClose={() => setActive(null)} />}
    </motion.div>
  );
}

function BlogArticleModal({ post, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center p-4 md:p-8 overflow-y-auto"
      style={{ background: "rgba(20,16,10,0.92)", backdropFilter: "blur(10px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="blog-article-modal"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="blog-article-close"
        className="fixed top-5 right-5 md:top-7 md:right-7 z-10 w-11 h-11 rounded-full flex items-center justify-center bg-[#EDE7D9] text-[#262016]"
        aria-label="Close article"
      >
        <X size={20} />
      </button>
      <article
        className="relative w-full max-w-3xl my-8 md:my-12 rp-foam p-7 md:p-12 bg-[#F6F1E5]"
        style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.4)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 text-[10px] tracking-[0.16em] uppercase mb-5">
          <span className="text-[#B5793F] inline-flex items-center gap-1.5">
            <BookOpen size={13} /> {post.readTime}
          </span>
          {post.date && <span className="text-[#8A7E6B]">{post.date}</span>}
          {post.tag && (
            <span className="px-2 py-0.5 rounded-full bg-[#EDE7D9] text-[#5C5042] border border-[#26201618]">
              {post.tag}
            </span>
          )}
        </div>
        <h2
          className="rp-display text-3xl md:text-5xl font-light text-[#262016] leading-[1.1] mb-7"
          style={{ textWrap: "balance" }}
        >
          {post.title}
        </h2>
        <div className="space-y-5 text-[#262016] text-base md:text-[17px] leading-relaxed" style={{ textWrap: "pretty" }}>
          {(post.body || []).map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
        <div className="mt-10 pt-6 border-t border-[#26201615] text-sm text-[#5C5042] rp-italic" style={{ textWrap: "balance" }}>
          Written by Rosh
        </div>
      </article>
    </div>
  );
}

// ---------- More Info ----------
function MoreInfoSubsection() {
  return (
    <motion.div {...fadeUp} data-testid="more-info-pane">
      <Eyebrow>More info</Eyebrow>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-6">
        <div className="lg:col-span-7">
          <p className="rp-block-title">Personal information</p>
          <dl className="rp-foam bg-white/65 p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3.5">
            {MOREINFO_CONTENT.personal.map(([k, v]) => (
              <div key={k}>
                <dt className="text-[10px] tracking-[0.16em] uppercase text-[#8A7E6B]">{k}</dt>
                <dd className="text-sm text-[#262016] mt-0.5">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="lg:col-span-5">
          <p className="rp-block-title">References</p>
          {MOREINFO_CONTENT.references.map((r, i) => (
            <div key={i} className="rp-foam bg-white/65 p-5">
              <p className="rp-display text-lg text-[#262016] leading-tight">{r.name}</p>
              <p className="text-xs text-[#5C5042] mt-1">{r.role}</p>
              <a
                href={r.contact}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 text-xs tracking-[0.14em] uppercase text-[#B5793F] hover:text-[#262016]"
                data-testid={`reference-contact-${i}`}
              >
                <MessageCircle size={13} /> Contact reference
              </a>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// Renders **bold** and [link](url) markdown fragments inline.
function renderInline(text) {
  if (!text) return null;
  const pattern = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  const parts = String(text).split(pattern);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i} className="font-semibold text-[#262016]">{p.slice(2, -2)}</strong>;
    }
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(p);
    if (link) {
      return (
        <a
          key={i}
          href={link[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-[#B5793F] decoration-[1.5px] underline-offset-[3px] text-[#262016] hover:text-[#B5793F] transition-colors"
        >
          {link[1]}
        </a>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

// ---------- Exposure & Social Impact (merged) ----------
function ExposureSocialSubsection() {
  const accentHex = {
    wood: "#B5793F",
    mint: "#23C795",
    blue: "#19B5DE",
    ink: "#262016",
  };
  const { socialImpact, mentorship, chapters } = EXPOSURE_SOCIAL_CONTENT;

  return (
    <motion.div {...fadeUp} data-testid="more-exposure-social">
      {/* Row 1 — Social Impact + Mentorship, side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
        {/* Social Impact — WEF Global Shapers */}
        <div
          className="rp-foam bg-white/65 p-6 md:p-7 flex flex-col relative overflow-hidden"
          data-testid="social-impact-card"
        >
          <span
            className="absolute top-0 left-7 w-12 h-1 rounded-b-full"
            style={{ background: accentHex.blue }}
          />
          <h4
            className="rp-display text-2xl md:text-3xl font-medium leading-[1.05] mb-1.5"
            style={{ color: accentHex.blue, textWrap: "balance" }}
          >
            {socialImpact.org}
          </h4>
          <p
            className="rp-display text-base md:text-lg text-[#262016] leading-snug mb-4"
            style={{ textWrap: "balance" }}
          >
            {socialImpact.title}
          </p>
          <p
            className="text-sm text-[#5C5042] leading-relaxed mb-4"
            style={{ textWrap: "pretty" }}
          >
            {renderInline(socialImpact.body)}
          </p>
          <ul className="space-y-2.5 text-sm text-[#262016]">
            {socialImpact.highlights.map((h, j) => (
              <li key={j} className="flex items-start gap-2">
                <span className="text-[#19B5DE] mt-0.5">✦</span>
                <span className="leading-relaxed" style={{ textWrap: "pretty" }}>{renderInline(h)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Mentorship — School of Passion */}
        <div
          className="rp-foam bg-white/65 p-6 md:p-7 flex flex-col relative overflow-hidden"
          data-testid="mentorship-card"
        >
          <span
            className="absolute top-0 left-7 w-12 h-1 rounded-b-full"
            style={{ background: accentHex.wood }}
          />
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4
                className="rp-display text-2xl md:text-3xl font-medium leading-[1.05] mb-1.5"
                style={{ color: accentHex.wood, textWrap: "balance" }}
              >
                {mentorship.org}
              </h4>
              <p
                className="rp-display text-base md:text-lg text-[#262016] leading-snug"
                style={{ textWrap: "balance" }}
              >
                {mentorship.title}
              </p>
            </div>
            {mentorship.metric && (
              <span
                className="rp-display rp-italic text-lg md:text-xl leading-none shrink-0"
                style={{ color: accentHex.wood, textWrap: "balance" }}
              >
                {mentorship.metric}
              </span>
            )}
          </div>
          <p
            className="text-sm text-[#5C5042] leading-relaxed mt-3 mb-4"
            style={{ textWrap: "pretty" }}
          >
            {renderInline(mentorship.body)}
          </p>
          <ul className="space-y-2.5 text-sm text-[#262016]">
            {mentorship.highlights.map((h, j) => (
              <li key={j} className="flex items-start gap-2">
                <span className="text-[#B5793F] mt-0.5">✦</span>
                <span className="leading-relaxed" style={{ textWrap: "pretty" }}>{renderInline(h)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Row 2 — Chapters */}
      <div className="mt-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {chapters.map((c, i) => (
            <div
              key={i}
              className="rp-foam bg-white/65 p-6 md:p-7 relative overflow-hidden flex flex-col"
              data-testid={`exposure-chapter-${i}`}
            >
              <span
                className="absolute top-0 left-7 w-12 h-1 rounded-b-full"
                style={{ background: accentHex[c.accent] }}
              />
              <h4
                className="rp-display text-xl md:text-2xl font-medium text-[#262016] leading-snug mb-3"
                style={{ textWrap: "balance" }}
              >
                {c.title}
              </h4>
              <p
                className="text-sm text-[#5C5042] leading-relaxed flex-1"
                style={{ textWrap: "pretty" }}
              >
                {renderInline(c.body)}
              </p>
              {c.hats && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {c.hats.map((h) => (
                    <span
                      key={h}
                      className="px-2.5 py-1 rounded-full text-[11px] bg-[#EDE7D9] text-[#262016] border border-[#26201618]"
                    >
                      {h}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// ---------- Router ----------
const MAP = {
  leadership: ExposureSocialSubsection,
  skills: SkillsSubsection,
  education: EducationSubsection,
  testimonials: TestimonialsSubsection,
  media: MediaSubsection,
  gallery: GallerySubsection,
  social: SocialSubsection,
  blog: BlogSubsection,
  moreinfo: MoreInfoSubsection,
};

export default function MoreSubsection({ id }) {
  const Cmp = MAP[id];
  if (!Cmp) {
    return (
      <div className="py-10 text-center text-sm text-[#8A7E6B]">
        Section not found.
      </div>
    );
  }
  return <Cmp />;
}
